import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSearch } from '@/shared/hooks';
import formatTagsAsOptions from '@/shared/utils/tags/formatTagsAsOptions';
import useTagsStore from '@pkg/entities/tags/useStore';
import { DesignEntity } from '@/lib/enums';

const DefaultEntityTagMap = new Map([
  [DesignEntity.ACTIVITY, []],
  [DesignEntity.GROUP, []],
  [DesignEntity.ROLE, []],
]);

export const TagContext = createContext();
export const useTagContext = () => useContext(TagContext);

const TagProvider = ({ children }) => {
  const ordered = useTagsStore((state) => state.ordered);
  const tagMap = useTagsStore((state) => state.tags?.map);
  const tagList = useTagsStore((state) => state.tags?.list);
  const tagIndex = useTagsStore((state) => state.index);
  const [entityTagMap, setEntityTagMap] = useState(DefaultEntityTagMap);
  const [tagOrderMap, setTagOrderMap] = useState(new Map());

  const searchTags = useSearch({
    searchList: tagList,
    searchMap: tagMap,
    searchIndex: tagIndex,
  });

  const search = (searchTerm, validationFn) => {
    return searchTags({ searchTerm, validationFn });
  };

  // @ToDo work out a better way to restrict setting tag options.
  useEffect(() => {
    if (!ordered) {
      return;
    }

    const activityTags = formatTagsAsOptions(ordered, DesignEntity.ACTIVITY);
    const roleTags = formatTagsAsOptions(ordered, DesignEntity.ROLE);
    const groupTags = formatTagsAsOptions(ordered, DesignEntity.GROUP);

    setEntityTagMap(
      new Map([
        [DesignEntity.ACTIVITY, activityTags.options],
        [DesignEntity.GROUP, groupTags.options],
        [DesignEntity.ROLE, roleTags.options],
      ])
    );
    setTagOrderMap(
      new Map([
        [DesignEntity.ACTIVITY, activityTags.tagOrder],
        [DesignEntity.GROUP, groupTags.tagOrder],
        [DesignEntity.ROLE, roleTags.tagOrder],
      ])
    );
  }, [ordered]);

  const context = useMemo(() => {
    return {
      entityTagMap,
      tagMap,
      tagOrderMap,
      search,
    };
  }, [entityTagMap, tagMap, tagOrderMap]);

  return <TagContext.Provider value={context}>{children}</TagContext.Provider>;
};

export default TagProvider;
